// Global command shortcuts (SINGLE SOURCE OF TRUTH)
export const GLOBAL_SHORTCUTS = {
  SEARCH: {
    keys: ['ctrl', 'k'],
    description: 'Open Global search',
  },
  SHORTCUTS: {
    keys: ['alt', 'x'],
    description: 'View all Shortcuts',
  },
  THEME: {
    keys: ['alt', 't'],
    description: 'Change theme',
  },
  MODE: {
    keys: ['alt', 'm'],
    description: 'Change mode',
  },
  LOGOUT: {
    keys: ['alt', 'l'],
    description: 'Logout',
  },
  REFRESH_INDEX_CUSTOMER: {
    keys: ['alt', 'r', 'c'],
    description: 'Refresh Index For Customer',
  },
  REFRESH_INDEX_ORDERS: {
    keys: ['alt', 'r', 'o'],
    description: 'Refresh Index For Orders',
  },
  REFRESH_INDEX_PRODUCTS: {
    keys: ['alt', 'r', 'p'],
    description: 'Refresh Index For Products',
  },
  DOWNLOADS: {
    keys: ['alt', ';'],
    description: 'Toggle Downloads',
  },
  // Search suggestions - global shortcuts to open search in specific contexts
  SEARCH_CUSTOMERS: {
    keys: ['alt', 's', 'c'],
    description: 'Search Customers',
  },
  SEARCH_ORDERS: {
    keys: ['alt', 's', 'o'],
    description: 'Search Orders',
  },
  SEARCH_PRODUCTS: {
    keys: ['alt', 's', 'p'],
    description: 'Search Products',
  },
  // Context-aware tab shortcuts
  NEXT_TAB: {
    keys: ['alt', 'ArrowRight'],
    description: 'Switch to next tab',
  },
  PREV_TAB: {
    keys: ['alt', 'ArrowLeft'],
    description: 'Switch to previous tab',
  },
  // Context-aware table shortcuts
  TABLE_SELECT_ALL: {
    keys: ['alt', 'a'],
    description: 'Select all table rows',
  },
  TABLE_DESELECT_ALL: {
    keys: ['alt', 'd'],
    description: 'Deselect all table rows',
  },
} as const;

// Navigation shortcuts (G for Go to + specific destination)
export const NAVIGATION_SHORTCUTS = {
  // Core Commerce
  HOME: {
    keys: ['g', 'h'],
    route: '/home',
    name: 'Home',
    icon: 'home',
  },
  ORDERS: {
    keys: ['g', 'o'],
    route: '/orders',
    name: 'Orders',
    icon: 'cart',
  },
  PRODUCTS: {
    keys: ['g', 'p', 'r'],
    route: '/products',
    name: 'Products',
    icon: 'box',
  },
  CATEGORY: {
    keys: ['g', 'p', 'c'],
    route: '/products/categories',
    name: 'Category',
    icon: 'list',
  },
  ATTRIBUTE: {
    keys: ['g', 'p', 'a'],
    route: '/products/attributes',
    name: 'Attributes',
    icon: 'tasks',
  },
  INVENTORY: {
    keys: ['g', 'p', 'i'],
    route: '/products/inventory',
    name: 'Inventory ',
    icon: 'inventory',
  },
  CUSTOMERS: {
    keys: ['g', 'c'],
    route: '/customers',
    name: 'Customers',
    icon: 'person',
  },
  VISITORS: {
    keys: ['g', 'v'],
    route: '/visitors',
    name: 'Visitors',
    icon: 'person',
  },
  ATTRIBUTES: {
    keys: ['g', 'c', 'a'],
    route: '/customers/attributes',
    name: 'Customer Attributes',
    icon: 'tasks',
  },
  SHIPPING: {
    keys: ['g', 's', 'h'],
    route: '/shipments',
    name: 'Shipments',
    icon: 'truck',
  },
  PAYMENTS: {
    keys: ['g', 'p', 'y'],
    route: '/payments',
    name: 'Payments',
    icon: 'bank',
  },
  LOYALTY: {
    keys: ['g', 'l'],
    route: '/loyalty',
    name: 'Loyalty',
    icon: 'star',
  },
  // Settings
  SETTINGS: {
    keys: ['g', 's', 't'],
    route: '/settings',
    name: 'Store Settings',
    icon: 'settings',
  },
  ORGANIZATION: {
    keys: ['g', 's', 'o'],
    route: '/organisation',
    name: 'Organisation Settings',
    icon: 'settings',
  },
  // Marketing
  COUPONS: {
    keys: ['g', 'm', 'c'],
    route: '/coupons',
    name: 'Coupons',
    icon: 'coupon',
  },
  PROMOTIONS: {
    keys: ['g', 'm', 'p'],
    route: '/promotions',
    name: 'Promotions',
    icon: 'percentage',
  },
  LIVE_EVENTS: {
    keys: ['g', 'a', 'l'],
    route: '/analytics/live-events',
    name: 'Live Events',
    icon: 'subscription',
  },
  MEDIA: {
    keys: ['g', 'i'],
    route: '/media',
    name: 'Media',
    icon: 'camera',
  },
} as const;

export type NavigationShortcutId = keyof typeof NAVIGATION_SHORTCUTS;

export interface NavigationInfo {
  name: string;
  keys: readonly string[];
  route: string;
  icon: string;
  tooltipText: string;
}

// Generate route mappings from navigation shortcuts
export const ROUTE_MAPPINGS = Object.entries(NAVIGATION_SHORTCUTS).reduce(
  (acc, [, value]) => {
    // Validate that each key is a single character to prevent collisions
    value.keys.forEach(key => {
      if (key.length !== 1) {
        throw new Error(
          `Navigation shortcut key '${key}' must be a single character to prevent collisions`
        );
      }
    });

    // Use pipe delimiter to prevent collisions (e.g., 'g|h' vs 'gh')
    acc[value.keys.join('|')] = value.route;
    return acc;
  },
  {} as Record<string, string>
);

// Utility functions for shortcuts
export class ShortcutsUtils {
  static isMac(): boolean {
    return typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  }

  static formatShortcut(keys: readonly string[]): string {
    const isMac = this.isMac();

    return keys
      .map(key => {
        const lowerKey = key.toLowerCase();
        switch (lowerKey) {
          case 'alt':
            return isMac ? '⌥' : 'Alt';
          case 'ctrl':
            return isMac ? '⌘' : 'Ctrl';
          case 'shift':
            return isMac ? '⇧' : 'Shift';
          default:
            return key.charAt(0).toUpperCase() + key.slice(1);
        }
      })
      .join(' + ');
  }

  static formatSequentialKeys(keys: readonly string[]): string {
    return keys.map(key => key.charAt(0).toUpperCase() + key.slice(1)).join(' → ');
  }

  static getShortcutCategories() {
    return [
      {
        name: 'Commands',
        shortcuts: [
          {
            name: GLOBAL_SHORTCUTS.SEARCH.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.SEARCH.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.SHORTCUTS.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.SHORTCUTS.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.SEARCH_CUSTOMERS.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.SEARCH_CUSTOMERS.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.SEARCH_ORDERS.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.SEARCH_ORDERS.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.SEARCH_PRODUCTS.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.SEARCH_PRODUCTS.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.THEME.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.THEME.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.MODE.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.MODE.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.LOGOUT.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.LOGOUT.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.REFRESH_INDEX_CUSTOMER.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.REFRESH_INDEX_CUSTOMER.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.REFRESH_INDEX_ORDERS.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.REFRESH_INDEX_ORDERS.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.REFRESH_INDEX_PRODUCTS.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.REFRESH_INDEX_PRODUCTS.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.DOWNLOADS.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.DOWNLOADS.keys),
          },
        ],
      },
      {
        name: 'Tab Navigation',
        shortcuts: [
          {
            name: GLOBAL_SHORTCUTS.NEXT_TAB.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.NEXT_TAB.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.PREV_TAB.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.PREV_TAB.keys),
          },
        ],
      },
      {
        name: 'Table Actions',
        shortcuts: [
          {
            name: GLOBAL_SHORTCUTS.TABLE_SELECT_ALL.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.TABLE_SELECT_ALL.keys),
          },
          {
            name: GLOBAL_SHORTCUTS.TABLE_DESELECT_ALL.description,
            shortcut: this.formatShortcut(GLOBAL_SHORTCUTS.TABLE_DESELECT_ALL.keys),
          },
        ],
      },
      {
        name: 'Navigation',
        shortcuts: Object.values(NAVIGATION_SHORTCUTS).map(nav => ({
          name: nav.name,
          shortcut: this.formatSequentialKeys(nav.keys),
        })),
      },
    ];
  }

  static getNavigationItems() {
    return Object.values(NAVIGATION_SHORTCUTS).map(nav => ({
      name: nav.name,
      icon: nav.icon,
      index: nav.name,
      route: nav.route.replace('/', ''),
      key: this.formatSequentialKeys(nav.keys),
      shortcut: [...nav.keys],
    }));
  }

  static getCommandItems() {
    return [
      {
        name: GLOBAL_SHORTCUTS.SHORTCUTS.description,
        icon: 'jam_computer_alt',
        index: 'Shortcuts',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.SHORTCUTS.keys),
        shortcut: [...GLOBAL_SHORTCUTS.SHORTCUTS.keys],
      },
      {
        name: GLOBAL_SHORTCUTS.REFRESH_INDEX_CUSTOMER.description,
        icon: 'refresh',
        index: 'RefreshCustomerIndex',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.REFRESH_INDEX_CUSTOMER.keys),
        shortcut: [...GLOBAL_SHORTCUTS.REFRESH_INDEX_CUSTOMER.keys],
      },
      {
        name: GLOBAL_SHORTCUTS.REFRESH_INDEX_ORDERS.description,
        icon: 'refresh',
        index: 'RefreshOrderIndex',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.REFRESH_INDEX_ORDERS.keys),
        shortcut: [...GLOBAL_SHORTCUTS.REFRESH_INDEX_ORDERS.keys],
      },
      {
        name: GLOBAL_SHORTCUTS.REFRESH_INDEX_PRODUCTS.description,
        icon: 'refresh',
        index: 'RefreshProductIndex',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.REFRESH_INDEX_PRODUCTS.keys),
        shortcut: [...GLOBAL_SHORTCUTS.REFRESH_INDEX_PRODUCTS.keys],
      },
      {
        name: GLOBAL_SHORTCUTS.THEME.description,
        icon: 'jam_computer_alt',
        index: 'Theme',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.THEME.keys),
        shortcut: [...GLOBAL_SHORTCUTS.THEME.keys],
      },
      {
        name: GLOBAL_SHORTCUTS.MODE.description,
        icon: 'convert_dark',
        index: 'Mode',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.MODE.keys),
        shortcut: [...GLOBAL_SHORTCUTS.MODE.keys],
      },
      {
        name: GLOBAL_SHORTCUTS.LOGOUT.description,
        icon: 'settings',
        index: 'Logout',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.LOGOUT.keys),
        shortcut: [...GLOBAL_SHORTCUTS.LOGOUT.keys],
      },
      {
        name: GLOBAL_SHORTCUTS.DOWNLOADS.description,
        icon: 'arrow_down_circle',
        index: 'ToggleDownloads',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.DOWNLOADS.keys),
        shortcut: [...GLOBAL_SHORTCUTS.DOWNLOADS.keys],
      },
    ];
  }

  static findNavigationByShortcut(shortcutId: string) {
    const navigations = this.getNavigationItems();
    // Use the same delimiter as ROUTE_MAPPINGS for consistency
    return navigations.find(n => n.shortcut?.join('|') === shortcutId);
  }

  static getSuggestionItems() {
    return [
      {
        name: GLOBAL_SHORTCUTS.SEARCH_CUSTOMERS.description,
        icon: 'person',
        index: 'Customers',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.SEARCH_CUSTOMERS.keys),
        shortcut: [...GLOBAL_SHORTCUTS.SEARCH_CUSTOMERS.keys],
      },
      {
        name: GLOBAL_SHORTCUTS.SEARCH_ORDERS.description,
        icon: 'cart',
        index: 'Orders',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.SEARCH_ORDERS.keys),
        shortcut: [...GLOBAL_SHORTCUTS.SEARCH_ORDERS.keys],
      },
      {
        name: GLOBAL_SHORTCUTS.SEARCH_PRODUCTS.description,
        icon: 'box',
        index: 'Products',
        key: this.formatShortcut(GLOBAL_SHORTCUTS.SEARCH_PRODUCTS.keys),
        shortcut: [...GLOBAL_SHORTCUTS.SEARCH_PRODUCTS.keys],
      },
    ];
  }

  static getNavigationInfo(shortcutName: NavigationShortcutId): NavigationInfo {
    const nav = NAVIGATION_SHORTCUTS[shortcutName];
    return {
      name: nav.name,
      keys: [...nav.keys],
      route: nav.route,
      icon: nav.icon,
      tooltipText: `${nav.name}\n${this.formatSequentialKeys(nav.keys)}`,
    };
  }
}
